import { AxiosProgressEvent } from 'axios';

import { IQueryRequest } from '../types/baseTypes';
import { MessagesListType } from '../types/communityMessagesTypes';
import { IFileAttachment } from '../types/fileTypes';
import {
  IServiceRequestMessage,
  IServiceRequestMessageRoom,
  IServiceRequestsFilters,
  ITQActivityLogsQuery,
  ITQActivityLogsResult,
  ITQCreateServiceRequestBody,
  ITQCreateServiceRequestResponseBody,
  ITQCreateServiceRequestResponseResult,
  ITQCreateTagBody,
  ITQCreateTagResult,
  ITQReadActivityLogBody,
  ITQServiceRequest,
  ITQServiceRequestDetails,
  ITQServiceRequestResponseDetailsResult,
  ITQServiceRequestResponsesQuery,
  ITQServiceRequestResponsesResult,
  ITQServiceRequestsResult,
  ITQServiceRequestStatisticsResult,
  ITQTagsQuery,
  ITQTagsResult,
  ITQTopContributorsQuery,
  ITQTopContributorsResult,
  ITQUpdateServiceRequestBody,
  ITQUpdateServiceRequestResponseBody,
  ITQUpdateServiceRequestResponseResult,
  ITQUpdateServiceRequestResult,
} from '../types/tqConnectTypes/service-requests';
import API from './';

export default class TQConnectServiceRequestAPI {
  static serviceRequestsRoot = '/api/v1/tq-connect/service-requests';

  static tagsRoot = '/api/v1/tq-connect/tags';

  static activityLogRoot = '/api/v1/tq-connect/activity-logs';

  static serviceRequestByIdRoot =
    '/api/v1/tq-connect/service-requests/:serviceRequestId';

  static serviceRequestResponsesRoot =
    '/api/v1/tq-connect/service-requests/:serviceRequestId/responses';

  static serviceRequestResponseByIdRoot =
    '/api/v1/tq-connect/service-requests/:serviceRequestId/responses/:serviceRequestResponseId';

  static serviceRequestChatRoomRoot =
    '/api/v1/tq-connect/service-requests/:serviceRequestId/chat/room';

  static serviceRequestChatRoomMessagesRoot =
    '/api/v1/tq-connect/service-requests/:serviceRequestId/chat/room/:roomId/messages';

  static topContributorsRoot = '/api/v1/tq-connect/leaderboard';

  /**
   * Get TQ Connect service requests (code = 200)
   */
  static getServiceRequests = (
    query: IServiceRequestsFilters & IQueryRequest,
  ) => {
    const {
      limit,
      page,
      type,
      statuses,
      visibility,
      sortBy,
      sortOrder,
      includedTags,
      excludedTags,
    } = query;

    const queryString = new URLSearchParams({
      ...(page && { page: String(page) }),
      ...(limit && { limit: String(limit) }),
      ...(type && { type }),
      ...(statuses?.length && { statuses: statuses.join(',') }),
      ...(visibility && { visibility }),
      ...(sortBy && { sortBy }),
      ...(sortOrder && { sortOrder }),
      ...(includedTags?.length && {
        includedTags: includedTags.map(tag => tag._id).join(','),
      }),
      ...(excludedTags?.length && {
        excludedTags: excludedTags.map(tag => tag._id).join(','),
      }),
    }).toString();

    return API.get<ITQServiceRequestsResult>(`${this.serviceRequestsRoot}${queryString ? `?${queryString}` : ''}`);

  };

  /**
   * Create TQ Connect service request (code = 201)
   */
  static createServiceRequest = (data: ITQCreateServiceRequestBody) => {
    const {
      attachments,
      title,
      description,
      tags,
      visibility,
      status,
      requestedTo,
    } = data;


    return API.post<ITQServiceRequest>(`${this.serviceRequestsRoot}`, {
      ...(attachments && { attachments }),
      title,
      description,
      tags: tags.map(tag => tag._id),
      visibility,
      status,
      ...(requestedTo && { requestedTo }),
    },);
  };

  /**
   * Get TQ Connect service request statistics (code = 200)
   */
  static getServiceRequestStatistics = () => {
    return API.get<ITQServiceRequestStatisticsResult>(`${this.serviceRequestsRoot}/statistics`);
  };

  /**
   * Get TQ Connect service request by ID (code = 200)
   */
  static getServiceRequestById = (serviceRequestId: string) => {
    return API.get<ITQServiceRequestDetails>(`${this.serviceRequestByIdRoot.replace(':serviceRequestId', serviceRequestId)}`);
  };

  /**
   * Update TQ Connect service request (code = 200)
   */
  static updateServiceRequest = (
    serviceRequestId: string,
    data: ITQUpdateServiceRequestBody,
  ) => {
    const { attachments, title, description, tags, visibility, status } = data;

    return API.patch<ITQUpdateServiceRequestResult>(`${this.serviceRequestByIdRoot.replace(':serviceRequestId', serviceRequestId)}`, {
      ...(attachments && { attachments }),
      ...(title && { title }),
      ...(description && { description }),
      ...(tags && { tags: tags.map(tag => tag._id) }),
      ...(visibility && { visibility }),
      ...(status && { status }),
    });
  };

  /**
   * Delete TQ Connect service request (code = 200)
   */
  static deleteServiceRequest = (serviceRequestId: string) => {
    return API.delete<{ message: string }>(`${this.serviceRequestByIdRoot.replace(':serviceRequestId', serviceRequestId)}`);
  };

  /**
   * Get TQ Connect service request chatroom details (code = 200)
   */
  static getServiceRequestChatRoom = (serviceRequestId: string) => {
    return API.get<IServiceRequestMessageRoom>(`${this.serviceRequestChatRoomRoot.replace(':serviceRequestId', serviceRequestId)}`);
  };

  /**
   * Get all messages service request chatroom (code = 200)
   */
  static getMessagesInServiceRequestChatRoom = (
    serviceRequestId: string,
    roomId: string,
    lastMessageId: string | null,
    data: Partial<IQueryRequest> & {
      listType: MessagesListType;
    },
  ) => {
    const { limit = 10, listType } = data;

    const query = new URLSearchParams({
      // ...(page && { page: String(page) }),
      limit: String(limit),
      ...(lastMessageId && { messageId: lastMessageId }),
      ...(listType && { mode: listType }),
    });

    return API.get<IServiceRequestMessage[]>(`${this.serviceRequestChatRoomMessagesRoot.replace(':serviceRequestId', serviceRequestId).replace(':roomId', roomId)}${query.size ? `?${query}` : ''}`);

  };

  /**
   * Get message details by ID in service request chatroom (code = 200)
   */
  static getServiceRequestChatRoomMessageById = (
    serviceRequestId: string,
    roomId: string,
    messageId: string,
  ) => {

    return API.get<IServiceRequestMessage>(`${this.serviceRequestChatRoomMessagesRoot.replace(':serviceRequestId', serviceRequestId).replace(':roomId', roomId)}/${messageId}`);
  };

  /**
   * Get TQ Connect service request responses (code = 200)
   */
  static getServiceRequestResponses = (
    serviceRequestId: string,
    query: ITQServiceRequestResponsesQuery,
  ) => {
    const { limit, page } = query;

    const queryString = new URLSearchParams({
      ...(limit && { limit: String(limit) }),
      ...(page && { page: String(page) }),
    }).toString();

    return API.get<ITQServiceRequestResponsesResult>(`${this.serviceRequestResponsesRoot.replace(':serviceRequestId', serviceRequestId)}${queryString ? `?${queryString}` : ''}`);
  };

  /**
   * Create TQ Connect service request response (code = 201)
   */
  static createServiceRequestResponse = (
    serviceRequestId: string,
    data: ITQCreateServiceRequestResponseBody,
  ) => {
    const { description, attachments } = data;

    return API.post<ITQCreateServiceRequestResponseResult>(`${this.serviceRequestResponsesRoot.replace(':serviceRequestId', serviceRequestId)}`, {
      description,
      ...(attachments && { attachments }),
    });
  };

  /**
   * Get TQ Connect service request response by ID (code = 200)
   */
  static getServiceRequestResponseById = (
    serviceRequestId: string,
    serviceRequestResponseId: string,
  ) => {

    return API.get<ITQServiceRequestResponseDetailsResult>(`${this.serviceRequestResponseByIdRoot.replace(':serviceRequestId', serviceRequestId).replace(':serviceRequestResponseId', serviceRequestResponseId)}`);
  };

  /**
   * Update TQ Connect service request response (code = 200)
   */
  static updateServiceRequestResponse = (
    serviceRequestId: string,
    serviceRequestResponseId: string,
    data: ITQUpdateServiceRequestResponseBody,
  ) => {
    const { status, rejectionReason, review } = data;

    return API.patch<ITQUpdateServiceRequestResponseResult>(`${this.serviceRequestResponseByIdRoot.replace(':serviceRequestId', serviceRequestId).replace(':serviceRequestResponseId', serviceRequestResponseId)}`, {
      status,
      ...(rejectionReason && { rejectionReason }),
      ...(review && { review }),
    });

  };

  /**
   * Upload attachment in a service request (code = 200)
   */
  static uploadAttachmentInServiceRequest = (
    files: File[],
    onUploadProgress?: (e: AxiosProgressEvent) => void,
  ) => {
    const formData = new FormData();

    files.forEach(file => {
      formData.append('files', file);
    });

    return API.post<IFileAttachment[]>(`${this.serviceRequestsRoot}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });

  };

  /**
   * Get TQ Connect tags (code = 200)
   */
  static getTags = (query: ITQTagsQuery) => {
    const { search, limit, page, creator } = query;

    const queryString = new URLSearchParams({
      ...(search && { search }),
      ...(limit && { limit: String(limit) }),
      ...(page && { page: String(page) }),
      ...(creator && { creator }),
    }).toString();

    return API.get<ITQTagsResult>(`${this.tagsRoot}${queryString ? `?${queryString}` : ''}`);
  };

  /**
   * Create TQ Connect tag (code = 201)
   */
  static createTag = (data: ITQCreateTagBody) => {
    const { name } = data;

    return API.post<ITQCreateTagResult>(`${this.tagsRoot}`, {
      name,
    });
  };

  /**
   * Delete TQ Connect tag (code = 200)
   */
  static deleteTag = (tagId: string) => {
    return API.delete<{ message: string }>(`${this.tagsRoot}/${tagId}`);
  };

  /**
   * Get TQ Connect activity logs (code = 200)
   */
  static getActivityLogs = (query: ITQActivityLogsQuery) => {
    const { page, limit } = query;

    const queryString = new URLSearchParams({
      ...(page && { page: String(page) }),
      ...(limit && { limit: String(limit) }),
    }).toString();

    return API.get<ITQActivityLogsResult>(`${this.activityLogRoot}${queryString ? `?${queryString}` : ''}`);
  };

  /**
   * Mark activity log as read (code = 200)
   */
  static markActivityLogAsRead = (data: ITQReadActivityLogBody) => {
    const { logId } = data;

    return API.patch<{ message: string }>(`${this.activityLogRoot}/read`, {
      logId,
    });
  };

  /**
   * Get TQ Connect leaderboard (top contributors) (code = 200)
   */
  static getTQConnectTopContributors = (query?: ITQTopContributorsQuery) => {
    const { limit, page, preferCachedData } = query || {};

    const queryString = new URLSearchParams({
      ...(limit && { limit: String(limit) }),
      ...(page && { page: String(page) }),
      ...(preferCachedData && { preferCachedData: String(preferCachedData) }),
    }).toString();

    return API.get<ITQTopContributorsResult>(`${this.topContributorsRoot}${queryString ? `?${queryString}` : ''}`);
  };
}
